import { DayOfWeek, DeadlineRule, DeadlineRuleType, CreateDeadlineRuleInput } from '@/types';

/**
 * 曜日ごとの締切設定（引取・搬入統合）
 */
export interface WeekdayRules {
  pickup: {
    id?: string;
    deadlineTime: string;
    isActive: boolean;
    isDeleted?: boolean;
  } | null;
  delivery: {
    id?: string;
    deadlineTime: string;
    isActive: boolean;
    isDeleted?: boolean;
  } | null;
}

/**
 * 全曜日の設定（月曜日始まり）
 */
export interface AllWeekdayRules {
  [DayOfWeek.MONDAY]: WeekdayRules;
  [DayOfWeek.TUESDAY]: WeekdayRules;
  [DayOfWeek.WEDNESDAY]: WeekdayRules;
  [DayOfWeek.THURSDAY]: WeekdayRules;
  [DayOfWeek.FRIDAY]: WeekdayRules;
  [DayOfWeek.SATURDAY]: WeekdayRules;
  [DayOfWeek.SUNDAY]: WeekdayRules;
}

/**
 * DeadlineRuleInlineEditorのプロパティ
 */
export interface DeadlineRuleInlineEditorProps {
  rules: DeadlineRule[];
  businessPartnerId: string;
  businessPartnerName: string;
  companyId: string;
  isLoading?: boolean;
}

/**
 * DeadlineRuleInlineEditorのref
 */
export interface DeadlineRuleInlineEditorRef {
  getModifiedRules: () => {
    toCreate: CreateDeadlineRuleInput[];
    toUpdate: Array<{ id: string; input: Partial<CreateDeadlineRuleInput> }>;
    toDelete: DeadlineRule[];
  };
  cancelChanges: () => void;
}

/**
 * 締切ルールの変更差分
 */
export interface DeadlineRuleModifications {
  toCreate: CreateDeadlineRuleInput[];
  toUpdate: Array<{ id: string; input: Partial<CreateDeadlineRuleInput> }>;
  toDelete: DeadlineRule[];
}

/**
 * 曜日別セクションのプロパティ
 */
export interface WeekdayRuleSectionProps {
  dayOfWeek: DayOfWeek;
  dayLabel: string;
  dayRules: WeekdayRules;
  isEnabled: boolean;
  isLoading?: boolean;
  onWeekdayToggle: (dayOfWeek: DayOfWeek, enabled: boolean) => void;
  onPickupToggle: (dayOfWeek: DayOfWeek, enabled: boolean) => void;
  onDeliveryToggle: (dayOfWeek: DayOfWeek, enabled: boolean) => void;
  onRuleUpdate: (
    ruleType: DeadlineRuleType,
    dayOfWeek: DayOfWeek,
    field: 'deadlineTime' | 'isActive',
    value: any
  ) => void;
}

/**
 * 時間入力フィールドのプロパティ
 */
export interface RuleTimeInputProps {
  ruleType: DeadlineRuleType;
  dayOfWeek: DayOfWeek;
  rule: {
    deadlineTime: string;
    isActive: boolean;
    isDeleted?: boolean;
  } | null;
  isLoading?: boolean;
  onChange: (value: string) => void;
}

/**
 * デフォルトの曜日ルール
 */
export const defaultWeekdayRules: WeekdayRules = {
  pickup: null,
  delivery: null,
};

/**
 * デフォルトの全曜日ルール
 */
export const createDefaultAllWeekdayRules = (): AllWeekdayRules => ({
  [DayOfWeek.MONDAY]: { ...defaultWeekdayRules },
  [DayOfWeek.TUESDAY]: { ...defaultWeekdayRules },
  [DayOfWeek.WEDNESDAY]: { ...defaultWeekdayRules },
  [DayOfWeek.THURSDAY]: { ...defaultWeekdayRules },
  [DayOfWeek.FRIDAY]: { ...defaultWeekdayRules },
  [DayOfWeek.SATURDAY]: { ...defaultWeekdayRules },
  [DayOfWeek.SUNDAY]: { ...defaultWeekdayRules },
});
